import React, { useEffect, useState } from 'react';
import InfoTip from '../components/InfoTip';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Gauge, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DetailsToggle from '../components/DetailsToggle';
import { navLabel } from '../utils/navigation';
import MonthPicker from '../components/MonthPicker';
import { apiService } from '../services/api';
import ProjectCard from '../components/compliance/ProjectCard';
import StandingChip from '../components/compliance/StandingChip';
import { Overview, STANDARDS, StandardId } from '../components/compliance/types';
import { furthestFirst } from '../components/compliance/model';
import * as fmt from '../lib/format';
import { shiftMonth } from '../utils/urlParams';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { useDetails } from '../hooks/useDetails';

// The projects shown before "Show all": the ones furthest from the standards.
const FIRST_SHOWN = 5;

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const ICON_BUTTON =
  'rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

// Compliance: how each project is doing against the four standards this month, for admins. Plain live
// numbers, each set against last month; pressing one opens that page for the project. No combined score.
export default function CompliancePage() {
  const navigate = useNavigate();
  // The month lives in the address, so a reload or a copied link opens the same month.
  const { period, current, isCurrent, setPeriod, projectKey, setProjectKey } = usePeriodParams();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Headline first: the five projects furthest from the standards; the rest are one press away (?details=1).
  const [showAll, setShowAll] = useDetails();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getComplianceOverview(period)
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setData(r.data);
          setError(null);
        } else setError(r.error || 'Could not load the compliance overview');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the compliance overview'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period, refreshKey]);

  const periodIsCurrent = isCurrent;
  const quarterLabel = data?.planning_quarter?.label ?? null;

  // Each page reads the month and project from the address, so the card lands on the same view.
  const open = (standard: StandardId, projectKey: string | null) => {
    const page = STANDARDS.find((s) => s.id === standard)!.page;
    const params = new URLSearchParams();
    if (standard === 'planning') {
      if (data?.planning_quarter) params.set('quarter', data.planning_quarter.id);
    } else params.set('period', period);
    if (projectKey) params.set('project_key', projectKey);
    navigate(`${page}?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/compliance')} subtitle="How each project is doing against the four standards" icon={Gauge} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <button type="button" aria-label="Previous month" onClick={() => setPeriod(shiftMonth(period, -1))} className={ICON_BUTTON}>
                <ChevronLeft className="h-4 w-4" />
              </button>
              <MonthPicker value={period} max={current} onChange={setPeriod} />
              <button type="button" aria-label="Next month" disabled={periodIsCurrent} onClick={() => setPeriod(shiftMonth(period, 1))} className={ICON_BUTTON}>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-500">
              Compared with {data ? fmt.month(data.previous_period) : 'last month'}
              {periodIsCurrent && ' · this month is still in progress'}
              <InfoTip id="compliance.change" />
            </p>
            <div className="ml-auto">
              <button type="button" onClick={() => setRefreshKey((n) => n + 1)} aria-label="Refresh" className={ICON_BUTTON}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin motion-reduce:animate-none' : ''}`} />
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">
              {error}
            </div>
          )}

          {loading && !data ? (
            <div className="space-y-4" role="status" aria-label="Loading the compliance overview">
              <div className={`${CARD} h-40 animate-pulse motion-reduce:animate-none`} />
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`${CARD} h-72 animate-pulse motion-reduce:animate-none`} />
                ))}
              </div>
            </div>
          ) : data ? (
            <div className={loading ? 'opacity-70 transition-opacity motion-reduce:transition-none' : ''}>
              <section className={`${CARD} mb-6 p-5`} aria-label={`All projects in ${fmt.month(data.period)}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex items-center">
                    <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">All projects, {fmt.month(data.period)}</h2>
                    <InfoTip id="compliance.standards" />
                  </div>
                  <span className="text-xs text-gray-500">{data.projects.length} {data.projects.length === 1 ? 'project' : 'projects'} with activity</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {STANDARDS.map((s) => (
                    <StandingChip
                      key={s.id}
                      standard={s.id}
                      standing={data.overall[s.id]}
                      planningQuarter={quarterLabel}
                      large
                      onPress={() => open(s.id, null)}
                      pressLabel={`Open ${s.label} for every project`}
                    />
                  ))}
                </div>
                <p className="mt-3 text-sm text-gray-700">{data.overall.sentence}</p>
              </section>

              {data.projects.length === 0 ? (
                <div className={`${CARD} px-6 py-12 text-center text-sm text-gray-600`}>No project has activity to judge in {fmt.month(data.period)}.</div>
              ) : (
                <>
                  {/* A link that names a project (?project_key=) opens that project's card alone, so what was asked for is never hidden among the rest. */}
                  {(() => {
                    const ordered = furthestFirst(data.projects);
                    const chosen = projectKey ? ordered.find((p) => p.project_key === projectKey) : undefined;
                    const shown = chosen ? [chosen] : ordered.slice(0, showAll ? undefined : FIRST_SHOWN);
                    return (
                      <>
                        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                          <h2 className="text-sm font-semibold text-gray-900">
                            {chosen ? chosen.project_name ?? chosen.project_key : showAll || data.projects.length <= FIRST_SHOWN ? 'Every project' : `The ${FIRST_SHOWN} furthest from the standards`}
                          </h2>
                          {chosen ? (
                            <button type="button" onClick={() => setProjectKey(null)} className="rounded px-1 py-1 text-sm font-medium text-blue-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                              Show every project
                            </button>
                          ) : (
                            <p className="text-sm text-gray-600">Furthest from 100% first.</p>
                          )}
                        </div>
                        <section id="compliance-projects" aria-label="Projects" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {shown.map((p) => (
                            <ProjectCard key={p.project_key} project={p} planningQuarter={quarterLabel} onOpen={open} />
                          ))}
                        </section>
                        {!chosen && data.projects.length > FIRST_SHOWN && (
                          <div className="mt-4">
                            <DetailsToggle
                              open={showAll}
                              onChange={setShowAll}
                              showLabel={`Show all ${fmt.count(data.projects.length)} projects`}
                              hideLabel={`Show only the ${FIRST_SHOWN} furthest`}
                              controls="compliance-projects"
                            />
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
